import type { Enum } from '@partou/core';
import {
  type DashboardObservationCounts,
  type DashboardStatistics,
  type DashboardStatisticsFilters,
  EMPTY_OBSERVATION_COUNTS,
  type Group,
} from '@partou/my-group/data';
import { MOCK_GROUPS } from './mock-groups.const';
import { MOCK_DASHBOARD_DELAY_KEY, MOCK_DASHBOARD_LOAD_KEY, MOCK_DASHBOARD_SCENARIO_KEY } from './mock-keys.const';

export function getMockDashboardDelayMs(): number {
  const stored: string | null = window.localStorage.getItem(MOCK_DASHBOARD_DELAY_KEY);
  const parsed = Number(stored);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export const MOCK_DASHBOARD_LOAD_OUTCOMES = ['success', 'error'] as const;

export type MockDashboardLoadOutcome = Enum<typeof MOCK_DASHBOARD_LOAD_OUTCOMES>;

export function getMockDashboardLoadOutcome(): MockDashboardLoadOutcome {
  const stored: string | null = window.localStorage.getItem(MOCK_DASHBOARD_LOAD_KEY);
  return stored === 'error' ? 'error' : 'success';
}

type DashboardStatisticsRow = Readonly<{
  group: Group;
  activeChildren: number;
  observations: DashboardObservationCounts;
}>;

const DEFAULT_ROWS: ReadonlyArray<DashboardStatisticsRow> = [
  {
    group: MOCK_GROUPS.vlindertjes,
    activeChildren: 14,
    observations: { dueWithinWeek: 3, dueWithinMonth: 2, deadlineBeyond: 6, upcoming: 1, completed: 22, expired: 0 },
  },
  {
    group: MOCK_GROUPS.bijtjes,
    activeChildren: 11,
    observations: { dueWithinWeek: 2, dueWithinMonth: 1, deadlineBeyond: 4, upcoming: 0, completed: 17, expired: 1 },
  },
  {
    group: MOCK_GROUPS.leeuwtjes,
    activeChildren: 16,
    observations: { dueWithinWeek: 4, dueWithinMonth: 0, deadlineBeyond: 7, upcoming: 3, completed: 19, expired: 0 },
  },
  {
    group: MOCK_GROUPS.kikkertjes,
    activeChildren: 8,
    observations: { dueWithinWeek: 0, dueWithinMonth: 1, deadlineBeyond: 2, upcoming: 2, completed: 9, expired: 0 },
  },
  {
    group: MOCK_GROUPS.vogeltjes,
    activeChildren: 13,
    observations: { dueWithinWeek: 1, dueWithinMonth: 3, deadlineBeyond: 5, upcoming: 1, completed: 14, expired: 0 },
  },
  {
    group: MOCK_GROUPS.olifantjes,
    activeChildren: 10,
    observations: { dueWithinWeek: 0, dueWithinMonth: 0, deadlineBeyond: 3, upcoming: 0, completed: 11, expired: 0 },
  },
  {
    group: MOCK_GROUPS.honden,
    activeChildren: 12,
    observations: { dueWithinWeek: 2, dueWithinMonth: 1, deadlineBeyond: 4, upcoming: 1, completed: 13, expired: 0 },
  },
  {
    group: MOCK_GROUPS.katjes,
    activeChildren: 9,
    observations: { dueWithinWeek: 1, dueWithinMonth: 0, deadlineBeyond: 1, upcoming: 0, completed: 7, expired: 0 },
  },
];

export const MOCK_DASHBOARD_SCENARIOS = {
  default: DEFAULT_ROWS,
} as const satisfies Record<string, ReadonlyArray<DashboardStatisticsRow>>;

export type MockDashboardScenarioName = keyof typeof MOCK_DASHBOARD_SCENARIOS;

export function getMockDashboardScenarioName(): MockDashboardScenarioName {
  const stored: string | null = window.localStorage.getItem(MOCK_DASHBOARD_SCENARIO_KEY);
  if (stored !== null && stored in MOCK_DASHBOARD_SCENARIOS) {
    return stored as MockDashboardScenarioName;
  }
  return 'default';
}

export function aggregateMockStatistics(
  rows: ReadonlyArray<DashboardStatisticsRow>,
  filters: DashboardStatisticsFilters,
): DashboardStatistics {
  const inScope: ReadonlyArray<DashboardStatisticsRow> = rows.filter((row: DashboardStatisticsRow): boolean => {
    if (filters.locationId !== null && row.group.location.id !== filters.locationId) return false;
    if (filters.groupId !== null && row.group.id !== filters.groupId) return false;
    return true;
  });
  const observations: DashboardObservationCounts = inScope.reduce(
    (totals: DashboardObservationCounts, row: DashboardStatisticsRow): DashboardObservationCounts => ({
      dueWithinWeek: totals.dueWithinWeek + row.observations.dueWithinWeek,
      dueWithinMonth: totals.dueWithinMonth + row.observations.dueWithinMonth,
      deadlineBeyond: totals.deadlineBeyond + row.observations.deadlineBeyond,
      upcoming: totals.upcoming + row.observations.upcoming,
      completed: totals.completed + row.observations.completed,
      expired: totals.expired + row.observations.expired,
    }),
    EMPTY_OBSERVATION_COUNTS,
  );
  return {
    resources: {
      locations: new Set(inScope.map((row: DashboardStatisticsRow): string => row.group.location.id)).size,
      groups: new Set(inScope.map((row: DashboardStatisticsRow): string => row.group.id)).size,
      children: inScope.reduce((total: number, row: DashboardStatisticsRow): number => total + row.activeChildren, 0),
    },
    observations,
  };
}
